import { CommandBus } from '../bridge/CommandBus';
import { coatPaletteFor } from '../art/palettes';
import type { PaletteInstance } from '../art/types';
import { coatPhenotype } from '../sim/ranch/genetics';
import { bindKeys } from '../input/InputController';
import { RanchRenderer } from '../render/RanchRenderer';
import type { MinigameId } from '../sim/minigames/framework';
import type { RanchCommand } from '../sim/ranch/commands';
import { FEED_PLANS, eventLeague, type FacilityId } from '../sim/ranch/economy';
import { seasonDayOf, seasonOf } from '../sim/ranch/horse';
import type { RanchEvent } from '../sim/ranch/types';
import type { StatId } from '../sim/race/types';
import { raceSetupFor } from '../sim/ranch/raceday';
import { saveGame } from '../sim/ranch/save';
import type { RanchSim } from '../sim/ranch/sim';
import { makeSnapshot, scheduleWindow } from '../sim/ranch/view';
import { showDaySummary, type DaySummary } from '../ui/DaySummaryModal';
import { RanchHud } from '../ui/RanchHud';
import type { ModalHandle } from '../ui/RanchModals';
import {
  showBarnModal,
  showFacilityModal,
  showFeedModal,
  showNoticeBoard,
  showPedigreeModal,
} from '../ui/RanchModals';
import { RaceScene } from './RaceScene';
import type { Scene, SceneContext } from './SceneManager';
import { createTrainingScene } from './TrainingSceneFactory';
import type { TrainingLaunch, TrainingSceneOptions } from './minigameCommon';

const AUTOSAVE_THROTTLE_MS = 45_000;

/** What the stable did today, accumulated from sim events and flushed
 * into the day's-end report when the calendar turns. */
interface DayLog {
  startDay: number;
  xp: Partial<Record<StatId, number>>;
  sessions: number;
  levelUps: { stat: StatId; level: number }[];
  races: { league: number; place: number; purseCents: number; dnf: boolean; horseName: string }[];
  notes: string[];
  moneyStart: number;
}

function emptyDayLog(moneyStart: number, startDay: number): DayLog {
  return { startDay, xp: {}, sessions: 0, levelUps: [], races: [], notes: [], moneyStart };
}

/** Home: the fixed ranch scene that ties the whole loop together. */
export class RanchScene implements Scene {
  private readonly sim: RanchSim;
  private readonly canPersist: boolean;
  private ctx!: SceneContext;
  private renderer!: RanchRenderer;
  private hud!: RanchHud;
  private bus!: CommandBus<RanchCommand>;
  private unbind: (() => void) | null = null;
  private modal: ModalHandle | null = null;
  private lastSaveMs = 0;
  private dayLog: DayLog = emptyDayLog(0, 0);
  private disposeSummary: (() => void) | null = null;
  private unbindSummary: (() => void) | null = null;
  private readonly onHide = (): void => {
    if (this.canPersist) saveGame(this.sim);
  };

  constructor(sim: RanchSim, canPersist: boolean) {
    this.sim = sim;
    this.canPersist = canPersist;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.bus = new CommandBus(this.sim);
    this.renderer = new RanchRenderer(ctx.scaler.root, {
      onFacility: (id) => this.openFacility(id),
      onBarn: () => this.openBarn(),
      onFeed: () => this.openModal(showFeedModal(ctx.uiRoot, this.sim, this.bus)),
      onBoard: () => this.openBoard(),
    });
    for (const id of ['gallopTrack', 'enduranceTrail', 'startingGate', 'roundPen'] as FacilityId[]) {
      this.renderer.setFacilityLevel(id, this.sim.state.facilities[id]);
    }
    this.renderer.setCoat(this.featuredPalette());
    this.hud = new RanchHud(ctx.uiRoot);
    this.dayLog = emptyDayLog(this.sim.state.economy.moneyCents, this.sim.state.meta.day);

    this.unbind = bindKeys([
      { code: 'Digit1', handler: () => this.openFacility('gallopTrack') },
      { code: 'Digit2', handler: () => this.openFacility('enduranceTrail') },
      { code: 'Digit3', handler: () => this.openFacility('startingGate') },
      { code: 'Digit4', handler: () => this.openFacility('roundPen') },
      { code: 'KeyB', handler: () => this.openBarn() },
      { code: 'KeyF', handler: () => this.openModal(showFeedModal(ctx.uiRoot, this.sim, this.bus)) },
      { code: 'KeyN', handler: () => this.openBoard() },
      { code: 'KeyE', handler: () => this.bus.dispatch({ kind: 'endDay' }) },
    ]);

    window.addEventListener('pagehide', this.onHide);
    document.addEventListener('visibilitychange', this.onHide);
  }

  frame(nowMs: number): void {
    const frame = this.sim.advance(nowMs);
    const events = this.sim.drainEvents();
    if (events.length > 0) {
      this.hud.handleEvents(events);
      this.modal?.refresh();
      for (const event of events) {
        this.logForSummary(event);
        if (event.kind === 'facilityUpgraded') {
          this.renderer.setFacilityLevel(event.facility, event.level);
        }
        if (event.kind === 'dayStarted') {
          // Everything from the day just ended is in the log by now —
          // the rollover events fire ahead of this one.
          this.showDayReport(event.day);
          if (this.canPersist) {
            saveGame(this.sim);
            this.lastSaveMs = nowMs;
          }
        }
        if (event.kind === 'trainingStarted') {
          this.pushTrainingScene(event.horseId, event.minigame, event.seed, event.tier, event.xpMult, event.mods);
        }
        if (
          event.kind === 'horsePromoted' ||
          event.kind === 'horseBought' ||
          event.kind === 'horseRetired'
        ) {
          this.renderer.setCoat(this.featuredPalette());
        }
      }
    }
    if (this.canPersist && nowMs - this.lastSaveMs > AUTOSAVE_THROTTLE_MS) {
      saveGame(this.sim);
      this.lastSaveMs = nowMs;
    }
    const snapshot = makeSnapshot(this.sim);
    this.hud.update(snapshot);
    this.renderer.update(nowMs / 1000, Boolean(snapshot.raceToday && !snapshot.raceToday.done));
    this.ctx.fps?.frame(nowMs, frame.steps, frame.droppedSeconds);
  }

  /** Accumulate the day's story as the sim tells it. */
  private logForSummary(event: RanchEvent): void {
    const nameOf = (horseId: number): string => this.sim.horseById(horseId)?.name ?? 'A horse';
    switch (event.kind) {
      case 'trainingResolved':
        this.dayLog.xp[event.stat] = (this.dayLog.xp[event.stat] ?? 0) + event.xp;
        this.dayLog.sessions += 1;
        break;
      case 'statLevel':
        this.dayLog.levelUps.push({ stat: event.stat, level: event.level });
        break;
      case 'raceRecorded': {
        const entry = this.sim.state.record.log[0];
        this.dayLog.races.push({
          league: eventLeague(event.eventId),
          place: event.place,
          purseCents: event.purseCents,
          dnf: entry ? entry.timeS === null : false,
          horseName: nameOf(event.horseId),
        });
        break;
      }
      case 'daysAdvanced':
        this.dayLog.notes.push(`The stable moved ahead from day ${event.fromDay} to day ${event.toDay}.`);
        break;
      case 'seasonRollover':
        this.dayLog.notes.push(`Season ${event.season} begins — fresh yearlings at the sale.`);
        break;
      case 'foalBorn':
        this.dayLog.notes.push(`A foal is born: ${event.name}.`);
        break;
      case 'lostAStep':
        this.dayLog.notes.push(`${nameOf(event.horseId)} has lost a step to the years.`);
        break;
      case 'horseRetired':
        if (event.forced) this.dayLog.notes.push(`${nameOf(event.horseId)} has been retired — age catches every horse.`);
        break;
      case 'horseInjured':
        this.dayLog.notes.push(`${nameOf(event.horseId)} pulled up sore — ${event.days} days of rest.`);
        break;
      case 'horseHealed':
        this.dayLog.notes.push(`${nameOf(event.horseId)} is sound again.`);
        break;
      case 'leagueUnlocked':
        this.dayLog.notes.push('A new circuit has opened up!');
        break;
      case 'feedDowngraded':
        this.dayLog.notes.push('Feed money ran dry — back to free pasture.');
        break;
      case 'derbyWon':
        this.dayLog.notes.push('GRAND DERBY CHAMPION — the bloodline is made.');
        break;
      default:
        break;
    }
  }

  /** The day's-end report, then a clean log for the new day. */
  private showDayReport(newDay: number): void {
    const state = this.sim.state;
    const horse = this.sim.featured();
    const todayCard = scheduleWindow(this.sim, 0).find((card) => card.isToday && card.entered);
    const nextEntry = state.entries
      .map((entry) => Math.floor(entry.eventId / 10))
      .filter((day) => day > newDay)
      .sort((a, b) => a - b)[0];
    const plan = FEED_PLANS[state.economy.feedPlan];

    const summary: DaySummary = {
      day: this.dayLog.startDay,
      dayEnd: newDay - 1,
      newDay,
      season: seasonOf(newDay),
      seasonDay: seasonDayOf(newDay),
      xp: this.dayLog.xp,
      sessions: this.dayLog.sessions,
      levelUps: this.dayLog.levelUps,
      races: this.dayLog.races,
      notes: this.dayLog.notes,
      feedName: plan.name,
      feedCostCents: plan.costCents,
      energy: state.energy.max,
      moneyCents: state.economy.moneyCents,
      moneyDeltaCents: state.economy.moneyCents - this.dayLog.moneyStart,
      raceToday: todayCard
        ? {
            league: todayCard.league,
            className: todayCard.className,
            distanceM: todayCard.distanceM,
            going: todayCard.going,
          }
        : null,
      nextRaceDay: nextEntry ?? null,
      horseName: horse?.name ?? 'your horse',
      injuryDays: horse?.injury?.daysLeft ?? 0,
    };

    this.dayLog = emptyDayLog(state.economy.moneyCents, newDay);
    this.modal?.dispose();
    this.modal = null;
    this.disposeSummary?.();
    const close = (): void => {
      this.disposeSummary?.();
      this.disposeSummary = null;
      this.unbindSummary?.();
      this.unbindSummary = null;
    };
    this.disposeSummary = showDaySummary(this.ctx.uiRoot, summary, close);
    this.unbindSummary = bindKeys([
      { code: 'Enter', inModal: true, handler: close },
      { code: 'Escape', inModal: true, handler: close },
      { code: 'Space', inModal: true, handler: close },
    ]);
  }

  private openModal(handle: ModalHandle): void {
    this.modal?.dispose();
    this.modal = handle;
  }

  private openBarn(): void {
    this.openModal(
      showBarnModal(this.ctx.uiRoot, this.sim, this.bus, {
        onPedigree: (horseId) => {
          this.openModal(
            showPedigreeModal(this.ctx.uiRoot, this.sim, horseId, {
              onBack: () => this.openBarn(),
            }),
          );
        },
      }),
    );
  }

  private openBoard(): void {
    this.openModal(
      showNoticeBoard(this.ctx.uiRoot, this.sim, this.bus, {
        onRide: (eventId) => this.ride(eventId),
      }),
    );
  }

  private openFacility(id: FacilityId): void {
    this.openModal(
      showFacilityModal(this.ctx.uiRoot, this.sim, this.bus, id, {
        onTrain: () => this.requestTraining(id),
      }),
    );
  }

  private requestTraining(facility: FacilityId): void {
    const horse = this.sim.featured();
    const minigame = this.sim.nextTrainingMode(facility, horse?.id ?? null);
    if (!horse || !minigame) return;
    if (this.sim.hasEnteredRaceToday(horse.id)) {
      this.hud.toast('No training on race day.', 'bad');
      return;
    }
    this.modal?.dispose();
    this.modal = null;
    // The sim escrows energy and draws the seed; the trainingStarted
    // event pushes the scene next frame.
    this.bus.dispatch({ kind: 'startTraining', facility });
  }

  /** The featured horse's genetic coat as a render palette. */
  private featuredPalette(): PaletteInstance | null {
    return this.horsePalette(this.sim.state.featuredId);
  }

  private horsePalette(horseId: number | null): PaletteInstance | null {
    const horse = horseId === null ? null : this.sim.horseById(horseId);
    return horse ? coatPaletteFor(coatPhenotype(horse.genotype.coat)) : null;
  }

  private pushTrainingScene(
    horseId: number,
    minigame: MinigameId,
    seed: number,
    tier: number,
    xpMult: number,
    mods?: Record<string, number>,
  ): void {
    const launch: TrainingLaunch = {
      seed,
      tier,
      xpMult,
      mods,
      palette: this.horsePalette(horseId) ?? undefined,
    };
    const options: TrainingSceneOptions = {
      onDone: (tape) => this.bus.dispatch({ kind: 'resolveTraining', tape }),
    };
    this.ctx.scenes.push(createTrainingScene(minigame, launch, options));
  }

  private ride(eventId: number): void {
    const card = scheduleWindow(this.sim, 0).find((c) => c.eventId === eventId && c.canRide);
    if (!card) return;
    this.modal?.dispose();
    this.modal = null;
    this.ctx.scenes.push(
      new RaceScene(raceSetupFor(this.sim, eventId), {
        backLabel: 'Back to the ranch',
        playerPalette: this.horsePalette(this.sim.entryForEvent(eventId)?.horseId ?? null) ?? undefined,
        onDone: (result) =>
          this.bus.dispatch({
            kind: 'applyRaceResult',
            eventId,
            place: result.playerPlace,
            timeS: result.playerTimeS,
            tape: result.tape,
            exhausted: result.exhaustedCount,
          }),
      }),
    );
  }

  suspend(): void {
    this.modal?.dispose();
    this.modal = null;
    this.disposeSummary?.();
    this.disposeSummary = null;
    this.unbindSummary?.();
    this.unbindSummary = null;
    this.renderer.visible = false;
    this.hud.visible = false;
    if (this.canPersist) saveGame(this.sim);
  }

  resume(): void {
    this.renderer.visible = true;
    this.hud.visible = true;
  }

  exit(): void {
    this.disposeSummary?.();
    this.unbindSummary?.();
    if (this.canPersist) saveGame(this.sim);
    window.removeEventListener('pagehide', this.onHide);
    document.removeEventListener('visibilitychange', this.onHide);
    this.unbind?.();
    this.modal?.dispose();
    this.hud.dispose();
    this.renderer.destroy();
  }
}
